// src/hooks/useLinkInBio.ts

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getLinkInBioPage,
  createLinkInBioPage,
  updateLinkInBioPage,
  deleteLinkInBioPage,
  getUploadUrl,
  getLinkInBioAnalytics,
  exportSignups,
  getSignupsEmailList,
  createLinkInBioTrackableUrl,
  deleteLinkInBioTrackableUrl,
} from '@/api/linkInBio';
import type { LinkInBioFormState } from '@/types/linkInBio';

/** Fetch the current user's link-in-bio page. */
export function useLinkInBioPage() {
  return useQuery({
    queryKey: ['link-in-bio'],
    queryFn: getLinkInBioPage,
    retry: (failureCount, error: any) => {
      // Don't retry on 404 (no page exists yet)
      if (error?.code === 'NOT_FOUND' || error?.status === 404) return false;
      return failureCount < 2;
    },
  });
}

/** Create a new link-in-bio page. */
export function useCreateLinkInBioPage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: LinkInBioFormState) => createLinkInBioPage(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['link-in-bio'] });
    },
  });
}

/** Update the existing link-in-bio page. */
export function useUpdateLinkInBioPage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<LinkInBioFormState>) => updateLinkInBioPage(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['link-in-bio'] });
    },
  });
}

/** Delete the link-in-bio page. */
export function useDeleteLinkInBioPage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteLinkInBioPage,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['link-in-bio'] });
    },
  });
}

/** Get a pre-signed upload URL. */
export function useGetUploadUrl() {
  return useMutation({
    mutationFn: ({ filename, contentType }: { filename: string; contentType: string }) =>
      getUploadUrl(filename, contentType),
  });
}

/** Fetch analytics data. */
export function useLinkInBioAnalytics(range: '7d' | '30d' = '30d') {
  return useQuery({
    queryKey: ['link-in-bio-analytics', range],
    queryFn: () => getLinkInBioAnalytics(range),
  });
}

/** Export signups as CSV. */
export function useExportSignups() {
  return useMutation({
    mutationFn: async () => {
      const blob = await exportSignups();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'signups.csv';
      a.click();
      URL.revokeObjectURL(url);
    },
  });
}

/** Download email list as a text file. */
export function useDownloadEmailList() {
  return useMutation({
    mutationFn: async () => {
      const emailList = await getSignupsEmailList();
      const blob = new Blob([emailList], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'signups-email-list.txt';
      a.click();
      URL.revokeObjectURL(url);
    },
  });
}

/** Create a trackable URL for the link-in-bio page. */
export function useCreateLinkInBioTrackableUrl() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => createLinkInBioTrackableUrl(name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['link-in-bio'] });
      queryClient.invalidateQueries({ queryKey: ['link-in-bio-analytics'] });
    },
  });
}

/** Delete a trackable URL from the link-in-bio page. */
export function useDeleteLinkInBioTrackableUrl() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (trackingId: string) => deleteLinkInBioTrackableUrl(trackingId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['link-in-bio'] });
      queryClient.invalidateQueries({ queryKey: ['link-in-bio-analytics'] });
    },
  });
}
